import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * ZeroGravityVoidBreakout — Momentum Breakout
 *
 * How it works:
 * - Option Sellers build "walls" at the Resistance (highest CE volume/OI) and Support strikes.
 * - When the index breaks completely outside of these walls, it enters a "void" where gamma squeeze is massive.
 * - Buys ATM CE if Index LTP > (Resistance Strike + 1 Strike Gap).
 * - Buys ATM PE if Index LTP < (Support Strike - 1 Strike Gap).
 */
export const evaluateZeroGravityVoidBreakout: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap, activeRange } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const minStrike = atmStrike - (activeRange * gap);
    const maxStrike = atmStrike + (activeRange * gap);
    
    const inRange = liveFlatOpts.filter((opt: any) => opt.strike >= minStrike && opt.strike <= maxStrike);
    
    const peOpts = inRange.filter((o: any) => o.type === 'PE').sort((a: any, b: any) => (b.volume || 0) - (a.volume || 0));
    const ceOpts = inRange.filter((o: any) => o.type === 'CE').sort((a: any, b: any) => (b.volume || 0) - (a.volume || 0));
    
    const supportStrike = peOpts[0]?.strike || 0;
    const resistanceStrike = ceOpts[0]?.strike || 0;

    if (!supportStrike || !resistanceStrike) return null;

    let status: 'BULL' | 'BEAR' | 'Sideways' = 'Sideways';
    let remarks = '';

    // Breakout above resistance wall
    if (spot > resistanceStrike + gap) {
        status = 'BULL';
        remarks = `Zero-Gravity Breakout above Resistance (${resistanceStrike})`;
    }
    // Breakdown below support wall
    else if (spot < supportStrike - gap) {
        status = 'BEAR';
        remarks = `Zero-Gravity Breakdown below Support (${supportStrike})`;
    }

    if (status === 'Sideways') return null;

    const optionType = status === 'BULL' ? 'CE' : 'PE';

    let entryPrice = 0;
    const atmRow = liveFlatOpts.find((r: any) => r.strike === atmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status,
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: remarks
    };
};
